import React, { useEffect, useMemo, useState } from 'react';
import {
  Save,
  Check,
  SlidersHorizontal,
  Building2,
  Shield,
  Users,
  Search,
  X,
  ChevronDown,
  Coins,
  Settings2,
  Plus,
  AlertCircle,
  Braces,
  Loader2
} from 'lucide-react';
import { ingestionService } from '../../../../components/api/ingestionService';
import {
  embeddingProviderService,
  type EmbeddingProvider
} from '../../../../components/api/embeddingProviderService';
import { apiService } from '../../../../components/api/apiService';
import { authservice } from '../../../../components/api/authservice';
import {
  chatAgentService,
  type ChatAgentAccessOverrideDto,
  type ChatAgentSettingsDto,
  type UpdateChatAgentSettingsDto
} from './ChatIntents/chatAgentService';

export type Theme = 'dark' | 'light';
export type DefaultTheme = 'dark' | 'light';
export type DefaultLanguage = 'en' | 'ar';

export type AccessCapability =
  | 'search'
  | 'report'
  | 'action'
  | 'liveAnalytics'
  | 'knowledgeBasePanel'
  | 'actionPanel'
  | 'analyticsHubPanel';

export interface AccessFlags {
  search: boolean;
  report: boolean;
  action: boolean;
  liveAnalytics: boolean;
  knowledgeBasePanel: boolean;
  actionPanel: boolean;
  analyticsHubPanel: boolean;
}

export interface AccessEntry {
  id: string;
  name: string;
  subtitle?: string;
  access: AccessFlags;
  /** Max tokens for this principal; null = no override (use agent default) */
  maxTokens: number | null;
}

export interface AgentFeatureSettings {
  // Group 1 Agent Defaults
  defaultTheme: DefaultTheme;
  defaultLanguage: DefaultLanguage;
  allowFallback: boolean;
  vectorStore: string;
  embeddingModel: string;
  kbSimilarityThresholdPreset: string;
  kbSimilarityThreshold: number;
  // Group 2 Core Modules (defaults)
  enableSearch: boolean;
  enableReports: boolean;
  enableLiveReports: boolean;
  enableActions: boolean;
  // Group 3 Workspace Panels (defaults)
  showKnowledgeBasePanel: boolean;
  showActionPanel: boolean;
  showAnalyticsHubPanel: boolean;
  // Group 4 Token ceiling for this agent
  maxTotalTokens: number | null;
  // Advanced orchestrator / KB overrides (JSON-editable)
  advancedSettings: AdvancedSettingsMap;
}

export type AdvancedSettingValue = string | number | boolean;
export type AdvancedSettingsMap = Record<string, AdvancedSettingValue>;

export interface ChatAgentSettingProps {
  theme?: Theme;
  agentId?: string;
}

const THEME_CONFIG: Record<Theme, {
  primary: string;
  secondary: string;
  background: string;
  surface: string;
  border: string;
  text: { primary: string; secondary: string; muted: string };
  accent: string;
  hover: string;
  input: string;
}> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: {
      primary: 'text-white',
      secondary: 'text-neutral-300',
      muted: 'text-neutral-400'
    },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-800',
    input: 'bg-neutral-700 border-neutral-600 text-white'
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: {
      primary: 'text-gray-900',
      secondary: 'text-gray-700',
      muted: 'text-gray-500'
    },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900'
  }
};

const MODULE_CAPABILITIES: { key: AccessCapability; label: string }[] = [
  { key: 'search', label: 'Search' },
  { key: 'report', label: 'Report' },
  { key: 'action', label: 'Action' },
  { key: 'liveAnalytics', label: 'Live Analytics' }
];

const PANEL_CAPABILITIES: { key: AccessCapability; label: string }[] = [
  { key: 'knowledgeBasePanel', label: 'KB Panel' },
  { key: 'actionPanel', label: 'Action Panel' },
  { key: 'analyticsHubPanel', label: 'Analytics Panel' }
];

const ALL_ACCESS: AccessFlags = {
  search: true,
  report: true,
  action: true,
  liveAnalytics: true,
  knowledgeBasePanel: true,
  actionPanel: true,
  analyticsHubPanel: true
};

const NONE_ACCESS: AccessFlags = {
  search: false,
  report: false,
  action: false,
  liveAnalytics: false,
  knowledgeBasePanel: false,
  actionPanel: false,
  analyticsHubPanel: false
};

const CHAT_AGENT_MODULE_ID = 'd4c3b2a1-5e6f-4d3c-9b2a-3c4d5e6f7a8b';
const MASTERDATA_ASSIGNMENTS_PATH = '/api/MasterData';
const MASTERDATA_DEPARTMENTS_URL = '/api/MasterData/departments';

interface DepartmentCatalogItem {
  id: string;
  name: string;
}

interface UserCatalogItem {
  id: string;
  name: string;
  email: string;
}

interface RoleCatalogItem {
  id: string;
  name: string;
  description?: string;
}

interface AccessCatalog {
  departments: DepartmentCatalogItem[];
  users: UserCatalogItem[];
  roles: RoleCatalogItem[];
}

interface AssignmentIds {
  departments: string[];
  users: string[];
  roles: string[];
}

type AccessOverridesDto = ChatAgentSettingsDto['accessOverrides'];

const EMPTY_ACCESS_OVERRIDES: AccessOverridesDto = {
  departments: [],
  users: [],
  roles: []
};

const EMPTY_ACCESS_CATALOG: AccessCatalog = {
  departments: [],
  users: [],
  roles: []
};

const normalizeApiList = (response: unknown): unknown[] => {
  if (Array.isArray(response)) return response;
  if (response && typeof response === 'object') {
    const maybeData = (response as { data?: unknown }).data;
    if (Array.isArray(maybeData)) return maybeData;
  }
  return [];
};

const parseAssignmentsResponse = (data: unknown): AssignmentIds => {
  const body =
    data && typeof data === 'object' && 'data' in data
      ? (data as { data: unknown }).data
      : data;
  if (!body || typeof body !== 'object') {
    return { departments: [], users: [], roles: [] };
  }
  const record = body as Record<string, unknown>;
  return {
    departments: Array.isArray(record.departments) ? (record.departments as string[]) : [],
    users: Array.isArray(record.users) ? (record.users as string[]) : [],
    roles: Array.isArray(record.roles) ? (record.roles as string[]) : []
  };
};

const normalizeAccessOverrides = (
  value: ChatAgentSettingsDto['accessOverrides'] | null | undefined
): AccessOverridesDto => {
  if (!value) return { ...EMPTY_ACCESS_OVERRIDES };
  return {
    departments: Array.isArray(value.departments) ? value.departments : [],
    roles: Array.isArray(value.roles) ? value.roles : [],
    users: Array.isArray(value.users) ? value.users : []
  };
};

const parseDepartmentCatalog = (data: unknown): DepartmentCatalogItem[] =>
  normalizeApiList(data)
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map(item => ({
      id: String(item.id ?? ''),
      name: String(item.name ?? item.id ?? '').trim()
    }))
    .filter(item => item.id);

const parseUserCatalog = (data: unknown): UserCatalogItem[] =>
  normalizeApiList(data)
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map(item => {
      const id = String(item.id ?? '');
      const email = typeof item.email === 'string' ? item.email.trim() : '';
      const firstName = typeof item.firstName === 'string' ? item.firstName.trim() : '';
      const lastName = typeof item.lastName === 'string' ? item.lastName.trim() : '';
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      return {
        id,
        name: fullName || email || id,
        email: email || fullName || id
      };
    })
    .filter(item => item.id);

const parseRoleCatalog = (data: unknown): RoleCatalogItem[] =>
  normalizeApiList(data)
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map(item => {
      const id = String(item.roleId ?? item.id ?? '');
      const name = String(item.roleName ?? item.name ?? id).trim();
      const description =
        typeof item.description === 'string' ? item.description.trim() : undefined;
      return { id, name, description };
    })
    .filter(item => item.id);

const createAccessEntry = (
  id: string,
  name: string,
  subtitle: string | undefined,
  existing?: AccessEntry
): AccessEntry => ({
  id,
  name,
  subtitle,
  access: existing?.access ? { ...existing.access } : { ...ALL_ACCESS },
  maxTokens: existing?.maxTokens ?? null
});

const mapAssignmentIdsToEntries = (
  ids: string[],
  resolve: (id: string) => { name: string; subtitle?: string },
  existing: AccessEntry[],
  overrides: ChatAgentAccessOverrideDto[] = []
): AccessEntry[] => {
  const existingById = new Map(existing.map(entry => [entry.id, entry]));
  const overridesById = new Map(overrides.map(override => [override.id, override]));
  const allIds = Array.from(new Set([...ids, ...overrides.map(override => override.id)]));

  return allIds.map(id => {
    const prev = existingById.get(id);
    const override = overridesById.get(id);
    const resolved = resolve(id);
    const entry = createAccessEntry(id, resolved.name, resolved.subtitle, prev);
    return override
      ? {
          ...entry,
          access: { ...override.capabilities },
          maxTokens: override.maxTokens
        }
      : entry;
  });
};

interface VectorStoreOption {
  id: string;
  label: string;
}

interface EmbeddingOption {
  id: string;
  label: string;
}

const vectorStoreLabel = (item: Record<string, unknown>): string => {
  const description = typeof item.description === 'string' ? item.description.trim() : '';
  const collectionName =
    typeof item.collectionName === 'string' ? item.collectionName.trim() : '';
  const provider = typeof item.provider === 'string' ? item.provider.trim() : '';
  const agentTitle = typeof item.agent_title === 'string' ? item.agent_title.trim() : '';
  const id = typeof item.id === 'string' ? item.id : '';
  return description || collectionName || provider || agentTitle || id || 'Unnamed vector store';
};

const embeddingOptionLabel = (provider: EmbeddingProvider): string => {
  const display = provider.displayName?.trim() || provider.name?.trim() || 'Embedding provider';
  const model = provider.defaultModel?.trim();
  return model ? `${display} · ${model}` : display;
};

const KB_SIMILARITY_CUSTOM_ID = 'custom';
const KB_SIMILARITY_CUSTOM_MIN = 0;
const KB_SIMILARITY_CUSTOM_MAX = 1;

const KB_SIMILARITY_PRESETS = [
  {
    id: 'search-discovery',
    label: 'Search / discovery copilot',
    min: 0.3,
    max: 0.4,
    default: 0.35,
    behavior: 'Prioritizes recall; can show broader matches'
  },
  {
    id: 'customer-support',
    label: 'Customer-support copilot',
    min: 0.4,
    max: 0.52,
    default: 0.46,
    behavior: 'Balanced recall and precision'
  },
  {
    id: 'internal-knowledge',
    label: 'Internal knowledge copilot',
    min: 0.45,
    max: 0.58,
    default: 0.515,
    behavior: 'Moderate confidence required'
  },
  {
    id: 'sales-crm',
    label: 'Sales / CRM copilot',
    min: 0.4,
    max: 0.55,
    default: 0.475,
    behavior: 'Allows fuzzy company and customer matching'
  },
  {
    id: 'coding',
    label: 'Coding copilot (internal docs)',
    min: 0.48,
    max: 0.62,
    default: 0.55,
    behavior: 'Exact technical context matters'
  },
  {
    id: 'finance-analytics',
    label: 'Finance / analytics copilot',
    min: 0.55,
    max: 0.7,
    default: 0.625,
    behavior: 'Prefer precise evidence; SQL should handle numbers'
  },
  {
    id: 'legal-compliance',
    label: 'Legal / compliance copilot',
    min: 0.6,
    max: 0.75,
    default: 0.675,
    behavior: 'Conservative retrieval and abstention'
  },
  {
    id: 'medical-safety',
    label: 'Medical / safety-critical copilot',
    min: 0.65,
    max: 0.8,
    default: 0.725,
    behavior: 'High precision, verified sources and refusal path'
  }
] as const;

const DEFAULT_ADVANCED_SETTINGS: AdvancedSettingsMap = {
  DB_BASED_RAG_SEARCH: false,
  ORCHESTRATOR_KB_CONTEXT_TOKEN_LIMIT: 1800,
  ORCHESTRATOR_KB_CHUNK_TOKEN_LIMIT: 600,
  ORCHESTRATOR_KB_MAX_CHUNKS: 5,
  ORCHESTRATOR_KB_QDRANT_LIMIT: 8,
  ORCHESTRATOR_KB_LLM_MAX_TOKENS: 800,
  ORCHESTRATOR_KB_HISTORY_TURNS: 2,
  DATA_LAKE_ROOT_PATH: 'D:\\SelficAI\\DataLake',
  STRUCTURED_QUERY_MAX_ROWS: 10000,
  STRUCTURED_QUERY_TIMEOUT_SECONDS: 30,
  STRUCTURED_QUERY_MAX_DATASETS: 10,
  KB_PRESENTATION_MAX_TABLE_ROWS: 10000
};

const DEFAULT_SETTINGS: AgentFeatureSettings = {
  defaultTheme: 'dark',
  defaultLanguage: 'en',
  allowFallback: false,
  vectorStore: '',
  embeddingModel: '',
  kbSimilarityThresholdPreset: 'customer-support',
  kbSimilarityThreshold: 0.46,
  enableSearch: true,
  enableReports: true,
  enableLiveReports: true,
  enableActions: true,
  showKnowledgeBasePanel: true,
  showActionPanel: true,
  showAnalyticsHubPanel: true,
  maxTotalTokens: 2_000_000,
  advancedSettings: { ...DEFAULT_ADVANCED_SETTINGS }
};

type Colors = (typeof THEME_CONFIG)[Theme];
type AccessLevel = 'departments' | 'roles' | 'users';
type AccessGrantFilter = 'all' | 'full' | 'partial' | 'none';

interface LevelFilterState {
  query: string;
  grant: AccessGrantFilter;
}

const EMPTY_LEVEL_FILTER: LevelFilterState = { query: '', grant: 'all' };

const countGranted = (access: AccessFlags): number =>
  (Object.keys(access) as (keyof AccessFlags)[]).filter((k) => access[k]).length;

const ACCESS_FLAG_COUNT = Object.keys(NONE_ACCESS).length;

const matchesGrantFilter = (access: AccessFlags, grant: AccessGrantFilter): boolean => {
  if (grant === 'all') return true;
  const granted = countGranted(access);
  if (grant === 'full') return granted === ACCESS_FLAG_COUNT;
  if (grant === 'none') return granted === 0;
  return granted > 0 && granted < ACCESS_FLAG_COUNT;
};

const filterAccessEntries = (
  entries: AccessEntry[],
  filter: LevelFilterState,
  level: AccessLevel
): AccessEntry[] => {
  const q = filter.query.trim().toLowerCase();
  return entries.filter((entry) => {
    if (!matchesGrantFilter(entry.access, filter.grant)) return false;
    if (!q) return true;
    const name = entry.name.toLowerCase();
    const subtitle = (entry.subtitle || '').toLowerCase();
    if (level === 'users') {
      return name.includes(q) || subtitle.includes(q);
    }
    if (level === 'roles') {
      return name.includes(q) || subtitle.includes(q);
    }
    // departments name or code
    return name.includes(q) || subtitle.includes(q);
  });
};

const LEVEL_FILTER_COPY: Record<
  AccessLevel,
  { placeholder: string; grantLabel: string }
> = {
  departments: {
    placeholder: 'Filter departments by name or code…',
    grantLabel: 'Department access'
  },
  roles: {
    placeholder: 'Filter roles by name or description…',
    grantLabel: 'Role access'
  },
  users: {
    placeholder: 'Filter users by name or email…',
    grantLabel: 'User access'
  }
};

const formatSimilarityRange = (min: number, max: number) =>
  `${min.toFixed(2)}–${max.toFixed(2)}`;

const clampSimilarity = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const parseSimilarityInput = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return clampSimilarity(n, KB_SIMILARITY_CUSTOM_MIN, KB_SIMILARITY_CUSTOM_MAX);
};

const formatAdvancedSettingsJson = (settings: AdvancedSettingsMap): string =>
  JSON.stringify(settings, null, 2);

const parseAdvancedSettingsJson = (
  raw: string
): { ok: true; value: AdvancedSettingsMap } | { ok: false; error: string } => {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ok: false, error: 'JSON must be a flat object with key-value pairs.' };
    }
    const result: AdvancedSettingsMap = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!key.trim()) {
        return { ok: false, error: 'Keys cannot be empty.' };
      }
      if (
        typeof value !== 'string' &&
        typeof value !== 'number' &&
        typeof value !== 'boolean'
      ) {
        return {
          ok: false,
          error: `"${key}" must be a string, number, or boolean.`
        };
      }
      result[key] = value;
    }
    return { ok: true, value: result };
  } catch {
    return { ok: false, error: 'Invalid JSON syntax. Check brackets, commas, and quotes.' };
  }
};

const coerceAdvancedValue = (
  raw: string,
  type: 'string' | 'number' | 'boolean'
): AdvancedSettingValue | null => {
  const trimmed = raw.trim();
  if (type === 'boolean') {
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    return null;
  }
  if (type === 'number') {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return trimmed;
};

const AdvancedSettingsModal: React.FC<{
  open: boolean;
  theme: Theme;
  colors: Colors;
  value: AdvancedSettingsMap;
  onClose: () => void;
  onSave: (value: AdvancedSettingsMap) => void;
}> = ({ open, theme, colors, value, onClose, onSave }) => {
  const [jsonText, setJsonText] = useState(() => formatAdvancedSettingsJson(value));
  const [error, setError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newType, setNewType] = useState<'string' | 'number' | 'boolean'>('string');
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setJsonText(formatAdvancedSettingsJson(value));
    setError(null);
    setNewKey('');
    setNewValue('');
    setNewType('string');
    setAddError(null);
  }, [open, value]);

  if (!open) return null;

  const handleAddKey = () => {
    const key = newKey.trim();
    if (!key) {
      setAddError('Key is required.');
      return;
    }
    const coerced = coerceAdvancedValue(newValue, newType);
    if (coerced === null) {
      setAddError(
        newType === 'boolean'
          ? 'Use true or false for boolean values.'
          : newType === 'number'
            ? 'Enter a valid number.'
            : 'Value is required.'
      );
      return;
    }
    const parsed = parseAdvancedSettingsJson(jsonText);
    const current = parsed.ok ? parsed.value : { ...value };
    if (key in current) {
      setAddError(`Key "${key}" already exists. Edit it in JSON below.`);
      return;
    }
    const next = { ...current, [key]: coerced };
    setJsonText(formatAdvancedSettingsJson(next));
    setNewKey('');
    setNewValue(newType === 'boolean' ? 'false' : '');
    setAddError(null);
    setError(null);
  };

  const handleSave = () => {
    const parsed = parseAdvancedSettingsJson(jsonText);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    onSave(parsed.value);
    onClose();
  };

  const handleFormat = () => {
    const parsed = parseAdvancedSettingsJson(jsonText);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setJsonText(formatAdvancedSettingsJson(parsed.value));
    setError(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="advanced-settings-title"
        onClick={e => e.stopPropagation()}
        className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border shadow-2xl overflow-hidden ${colors.border} ${colors.primary}`}
      >
        <div className={`flex items-start justify-between gap-3 px-5 py-4 border-b ${colors.border}`}>
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
              <Braces className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 id="advanced-settings-title" className={`text-base font-semibold ${colors.text.primary}`}>
                Advanced settings
              </h2>
              <p className={`text-xs ${colors.text.muted} mt-0.5`}>
                Orchestrator &amp; KB configuration as JSON. Add custom keys or edit values directly.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-2 rounded-lg border ${colors.border} ${colors.hover} ${colors.text.muted}`}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div
            className={`rounded-xl border p-3 space-y-2 ${colors.border} ${
              theme === 'light' ? 'bg-gray-50' : 'bg-neutral-900/50'
            }`}
          >
            <p className={`text-[11px] font-semibold uppercase tracking-wide ${colors.text.muted}`}>
              Add property
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                placeholder="KEY_NAME"
                className={`flex-1 min-w-0 ${colors.input} border rounded-md px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500`}
              />
              <select
                value={newType}
                onChange={e => {
                  const t = e.target.value as 'string' | 'number' | 'boolean';
                  setNewType(t);
                  setNewValue(t === 'boolean' ? 'false' : '');
                }}
                className={`sm:w-28 ${colors.input} border rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500`}
              >
                <option value="string">string</option>
                <option value="number">number</option>
                <option value="boolean">boolean</option>
              </select>
              <input
                type="text"
                value={newValue}
                onChange={e => setNewValue(e.target.value)}
                placeholder={newType === 'boolean' ? 'true / false' : 'value'}
                className={`flex-1 min-w-0 ${colors.input} border rounded-md px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500`}
              />
              <button
                type="button"
                onClick={handleAddKey}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium"
              >
                <Plus className="w-3.5 h-3.5" />
                Add
              </button>
            </div>
            {addError && (
              <p className="text-[11px] text-rose-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3 shrink-0" />
                {addError}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className={`text-xs font-medium ${colors.text.secondary}`}>Configuration JSON</p>
              <button
                type="button"
                onClick={handleFormat}
                className={`text-[11px] font-medium text-indigo-400 hover:text-indigo-300`}
              >
                Format JSON
              </button>
            </div>
            <div
              className={`rounded-xl border overflow-hidden ${colors.border} ${
                theme === 'dark' ? 'bg-[#0d1117]' : 'bg-[#f6f8fa]'
              }`}
            >
              <textarea
                value={jsonText}
                onChange={e => {
                  setJsonText(e.target.value);
                  setError(null);
                }}
                spellCheck={false}
                rows={18}
                className={`w-full resize-y min-h-[320px] px-4 py-3 text-[13px] leading-relaxed font-mono focus:outline-none ${
                  theme === 'dark'
                    ? 'bg-transparent text-emerald-300/95 placeholder-neutral-600'
                    : 'bg-transparent text-emerald-800 placeholder-gray-400'
                }`}
                aria-label="Advanced settings JSON"
              />
            </div>
            {error && (
              <p className="mt-2 text-xs text-rose-400 flex items-start gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                {error}
              </p>
            )}
            <p className={`mt-2 text-[11px] ${colors.text.muted}`}>
              Values must be string, number, or boolean. Nested objects are not supported.
            </p>
          </div>
        </div>

        <div
          className={`flex items-center justify-end gap-2 px-5 py-4 border-t ${colors.border} ${colors.secondary}`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 rounded-md text-sm font-medium border ${colors.border} ${
              theme === 'light' ? 'bg-gray-100 text-gray-700' : 'bg-neutral-800 text-neutral-300'
            } ${colors.hover}`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 rounded-md text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save / Update
          </button>
        </div>
      </div>
    </div>
  );
};

const ToggleRow: React.FC<{
  label: string;
  description?: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  theme: Theme;
  colors: Colors;
}> = ({ label, description, enabled, onChange, theme, colors }) => (
  <div className={`flex items-start justify-between gap-4 py-3 border-b ${colors.border} last:border-b-0`}>
    <div className="flex-1 min-w-0">
      <label className={`text-sm font-medium ${colors.text.primary}`}>{label}</label>
      {description && (
        <p className={`text-xs ${colors.text.muted} mt-0.5`}>{description}</p>
      )}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
        enabled ? 'bg-indigo-600' : theme === 'light' ? 'bg-gray-200' : 'bg-neutral-600'
      }`}
    >
      <span
        aria-hidden
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

const SegmentedControl: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  colors: Colors;
  theme: Theme;
  ariaLabel: string;
}> = ({ options, value, onChange, colors, theme, ariaLabel }) => (
  <div
    role="group"
    aria-label={ariaLabel}
    className={`inline-flex rounded-md border ${colors.border} overflow-hidden`}
  >
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 text-xs font-medium transition-colors ${
            active
              ? 'bg-indigo-600 text-white'
              : `${colors.text.secondary} ${theme === 'light' ? 'bg-white hover:bg-gray-50' : 'bg-neutral-700 hover:bg-neutral-600'}`
          }`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const SettingGroup: React.FC<{
  title: string;
  description: string;
  colors: Colors;
  children: React.ReactNode;
  bodyClassName?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}> = ({
  title,
  description,
  colors,
  children,
  bodyClassName = 'px-4 py-1',
  collapsible = false,
  defaultExpanded = true
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className={`${colors.surface} rounded-lg border ${colors.border} overflow-hidden`}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className={`w-full px-4 py-3 text-left flex items-start justify-between gap-3 ${colors.secondary} ${
            expanded ? `border-b ${colors.border}` : ''
          } hover:opacity-95 transition-opacity`}
        >
          <div className="min-w-0">
            <h3 className={`text-sm font-semibold ${colors.text.primary}`}>{title}</h3>
            <p className={`text-xs ${colors.text.muted} mt-0.5`}>{description}</p>
          </div>
          <ChevronDown
            className={`w-4 h-4 shrink-0 mt-0.5 ${colors.text.muted} transition-transform ${
              expanded ? 'rotate-180' : ''
            }`}
          />
        </button>
      ) : (
        <div className={`px-4 py-3 border-b ${colors.border} ${colors.secondary}`}>
          <h3 className={`text-sm font-semibold ${colors.text.primary}`}>{title}</h3>
          <p className={`text-xs ${colors.text.muted} mt-0.5`}>{description}</p>
        </div>
      )}
      {(!collapsible || expanded) && <div className={bodyClassName}>{children}</div>}
    </div>
  );
};

const AccessCapabilityToggle: React.FC<{
  label: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  theme: Theme;
  colors: Colors;
}> = ({ label, enabled, onChange, theme, colors }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    aria-label={`${label}: ${enabled ? 'granted' : 'denied'}`}
    onClick={() => onChange(!enabled)}
    className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${
      enabled
        ? 'border-indigo-500/50 bg-indigo-600/20 text-indigo-300'
        : `${colors.border} ${colors.text.muted} ${theme === 'light' ? 'bg-gray-50' : 'bg-neutral-900/40'}`
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${enabled ? 'bg-indigo-400' : theme === 'light' ? 'bg-gray-300' : 'bg-neutral-600'}`}
    />
    {label}
  </button>
);

const CapabilityChipGroup: React.FC<{
  title: string;
  capabilities: { key: AccessCapability; label: string }[];
  access: AccessFlags;
  entryId: string;
  onToggle: (id: string, capability: AccessCapability, enabled: boolean) => void;
  onSetGroup: (id: string, keys: AccessCapability[], grantAll: boolean) => void;
  theme: Theme;
  colors: Colors;
}> = ({ title, capabilities, access, entryId, onToggle, onSetGroup, theme, colors }) => {
  const keys = capabilities.map((c) => c.key);
  const allGranted = keys.every((k) => access[k]);
  const noneGranted = keys.every((k) => !access[k]);

  return (
    <div className={`rounded-md border ${colors.border} px-2.5 py-2 ${theme === 'light' ? 'bg-gray-50/80' : 'bg-neutral-900/30'}`}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className={`text-[10px] font-semibold uppercase tracking-wide ${colors.text.muted}`}>
          {title}
        </p>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onSetGroup(entryId, keys, true)}
            className={`text-[10px] font-medium uppercase tracking-wide ${
              allGranted ? 'text-indigo-400' : colors.text.muted
            } hover:text-indigo-400`}
          >
            All
          </button>
          <span className={colors.text.muted}>·</span>
          <button
            type="button"
            onClick={() => onSetGroup(entryId, keys, false)}
            className={`text-[10px] font-medium uppercase tracking-wide ${
              noneGranted ? 'text-indigo-400' : colors.text.muted
            } hover:text-indigo-400`}
          >
            None
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {capabilities.map((cap) => (
          <AccessCapabilityToggle
            key={cap.key}
            label={cap.label}
            enabled={access[cap.key]}
            onChange={(enabled) => onToggle(entryId, cap.key, enabled)}
            theme={theme}
            colors={colors}
          />
        ))}
      </div>
    </div>
  );
};

const parseTokenLimit = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(/,/g, ''));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.floor(n);
};

const formatTokenLimitInput = (value: number | null): string =>
  value === null ? '' : String(value);

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SECRET_SETTING_KEY_PATTERN =
  /(^|_)(api_?key|password|credentials?|client_?secret|private_?key|access_?key)($|_)/i;

const toAccessOverrideDto = (
  entry: AccessEntry,
  agentMaxTokens: number | null
): ChatAgentAccessOverrideDto => ({
  id: entry.id,
  // The backend contract uses a non-null Int64. "Inherit" is materialized
  // as the chatbot limit in the persisted full-replacement document.
  maxTokens: entry.maxTokens ?? agentMaxTokens ?? 0,
  capabilities: { ...entry.access }
});

const validateSettingsPayload = (
  payload: UpdateChatAgentSettingsDto
): string | null => {
  if (!GUID_PATTERN.test(payload.chatbotId)) {
    return 'The chatbot ID is not a valid GUID.';
  }
  if (!GUID_PATTERN.test(payload.defaults.vectorStoreId)) {
    return 'Select a valid vector store.';
  }
  if (!GUID_PATTERN.test(payload.defaults.embeddingProviderId)) {
    return 'Select a valid embedding provider.';
  }
  if (
    payload.defaults.kbSimilarityThreshold < 0 ||
    payload.defaults.kbSimilarityThreshold > 1
  ) {
    return 'KB similarity threshold must be between 0 and 1.';
  }

  const overrides = [
    ...payload.accessOverrides.departments,
    ...payload.accessOverrides.roles,
    ...payload.accessOverrides.users
  ];
  const invalidId = overrides.find(override => !GUID_PATTERN.test(override.id));
  if (invalidId) {
    return `Access override ID "${invalidId.id}" is not a valid GUID.`;
  }
  const invalidLimit = overrides.find(
    override =>
      override.maxTokens <= 0 ||
      (payload.defaults.maxTotalTokens !== null &&
        override.maxTokens > payload.defaults.maxTotalTokens)
  );
  if (invalidLimit) {
    return payload.defaults.maxTotalTokens === null
      ? 'Set chatbot total tokens before saving inherited access override limits.'
      : 'Override token limits must be positive and cannot exceed chatbot total tokens.';
  }
  const secretKey = Object.keys(payload.advancedSettings).find(key =>
    SECRET_SETTING_KEY_PATTERN.test(key)
  );
  if (secretKey) {
    return `Advanced setting "${secretKey}" appears to contain a secret and cannot be saved here.`;
  }
  return null;
};

const AccessLevelBlock: React.FC<{
  entries: AccessEntry[];
  onToggle: (id: string, capability: AccessCapability, enabled: boolean) => void;
  onSetGroup: (id: string, keys: AccessCapability[], grantAll: boolean) => void;
  onMaxTokensChange: (id: string, maxTokens: number | null) => void;
  agentMaxTokens: number | null;
  theme: Theme;
  colors: Colors;
  emptyLabel: string;
}> = ({
  entries,
  onToggle,
  onSetGroup,
  onMaxTokensChange,
  agentMaxTokens,
  theme,
  colors,
  emptyLabel
}) => {
  if (entries.length === 0) {
    return (
      <p className={`text-xs ${colors.text.muted} py-6 text-center`}>{emptyLabel}</p>
    );
  }

  return (
    <div>
      {entries.map((entry) => (
        <div
          key={entry.id}
          className={`flex flex-col gap-3 py-3 border-b ${colors.border} last:border-b-0`}
        >
          <div className="min-w-0">
            <p className={`text-sm font-medium ${colors.text.primary} truncate`}>{entry.name}</p>
            {entry.subtitle && (
              <p className={`text-xs ${colors.text.muted} truncate`}>{entry.subtitle}</p>
            )}
          </div>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <CapabilityChipGroup
              title="Core Modules"
              capabilities={MODULE_CAPABILITIES}
              access={entry.access}
              entryId={entry.id}
              onToggle={onToggle}
              onSetGroup={onSetGroup}
              theme={theme}
              colors={colors}
            />
            <CapabilityChipGroup
              title="Workspace Panels"
              capabilities={PANEL_CAPABILITIES}
              access={entry.access}
              entryId={entry.id}
              onToggle={onToggle}
              onSetGroup={onSetGroup}
              theme={theme}
              colors={colors}
            />
            <div
              className={`rounded-md border ${colors.border} px-2.5 py-2 ${
                theme === 'light' ? 'bg-gray-50/80' : 'bg-neutral-900/30'
              }`}
            >
              <p className={`mb-1.5 text-[10px] font-semibold uppercase tracking-wide ${colors.text.muted}`}>
                Token Limit
              </p>
              <label htmlFor={`token-limit-${entry.id}`} className="sr-only">
                Max tokens for {entry.name}
              </label>
              <div className="relative">
                <Coins className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${colors.text.muted}`} />
                <input
                  id={`token-limit-${entry.id}`}
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={formatTokenLimitInput(entry.maxTokens)}
                  onChange={(e) => onMaxTokensChange(entry.id, parseTokenLimit(e.target.value))}
                  placeholder={
                    agentMaxTokens != null
                      ? `Inherit (${agentMaxTokens.toLocaleString()})`
                      : 'Unlimited'
                  }
                  className={`w-full ${colors.input} border rounded-md pl-8 pr-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => onMaxTokensChange(entry.id, null)}
                  className={`text-[10px] font-medium uppercase tracking-wide ${
                    entry.maxTokens === null ? 'text-indigo-400' : colors.text.muted
                  } hover:text-indigo-400`}
                >
                  Inherit
                </button>
                <span className={colors.text.muted}>·</span>
                <button
                  type="button"
                  onClick={() =>
                    onMaxTokensChange(
                      entry.id,
                      agentMaxTokens != null ? agentMaxTokens : null
                    )
                  }
                  disabled={agentMaxTokens == null}
                  className={`text-[10px] font-medium uppercase tracking-wide ${
                    agentMaxTokens != null && entry.maxTokens === agentMaxTokens
                      ? 'text-indigo-400'
                      : colors.text.muted
                  } hover:text-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-inherit`}
                  title={
                    agentMaxTokens == null
                      ? 'Set chatbot total tokens first'
                      : `Use chatbot max (${agentMaxTokens.toLocaleString()})`
                  }
                >
                  Max
                </button>
              </div>
              <p className={`text-[10px] mt-1.5 ${colors.text.muted}`}>
                {entry.maxTokens === null
                  ? 'Uses chatbot total token cap'
                  : agentMaxTokens != null && entry.maxTokens === agentMaxTokens
                      ? `At chatbot max (${entry.maxTokens.toLocaleString()})`
                      : `${entry.maxTokens.toLocaleString()} max tokens`}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ChatAgentSetting: React.FC<ChatAgentSettingProps> = ({
  theme = 'dark',
  agentId = ''
}) => {
  const colors = THEME_CONFIG[theme];
  const [settings, setSettings] = useState<AgentFeatureSettings>(DEFAULT_SETTINGS);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsDocumentLoading, setSettingsDocumentLoading] = useState(false);
  const [settingsVersion, setSettingsVersion] = useState(0);
  const [loadedAccessOverrides, setLoadedAccessOverrides] =
    useState<AccessOverridesDto>(EMPTY_ACCESS_OVERRIDES);
  const [accessLevel, setAccessLevel] = useState<AccessLevel>('departments');
  const [departments, setDepartments] = useState<AccessEntry[]>([]);
  const [roles, setRoles] = useState<AccessEntry[]>([]);
  const [users, setUsers] = useState<AccessEntry[]>([]);
  const [accessCatalog, setAccessCatalog] = useState<AccessCatalog>(EMPTY_ACCESS_CATALOG);
  const [assignmentIds, setAssignmentIds] = useState<AssignmentIds | null>(null);
  const [accessCatalogLoading, setAccessCatalogLoading] = useState(true);
  const [accessAssignmentsLoading, setAccessAssignmentsLoading] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [levelFilters, setLevelFilters] = useState<Record<AccessLevel, LevelFilterState>>({
    departments: { ...EMPTY_LEVEL_FILTER },
    roles: { ...EMPTY_LEVEL_FILTER },
    users: { ...EMPTY_LEVEL_FILTER }
  });
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [vectorStoreOptions, setVectorStoreOptions] = useState<VectorStoreOption[]>([]);
  const [embeddingOptions, setEmbeddingOptions] = useState<EmbeddingOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  useEffect(() => {
    if (!agentId) return;

    let cancelled = false;
    setSettingsDocumentLoading(true);
    setSettingsError(null);

    chatAgentService
      .getSettings(agentId)
      .then((response: unknown) => {
        if (cancelled) return;
        const body =
          response && typeof response === 'object' && 'data' in response
            ? (response as { data?: unknown }).data
            : response;
        if (!body || typeof body !== 'object') {
          throw new Error('Invalid settings response.');
        }

        const document = body as ChatAgentSettingsDto;
        setSettingsVersion(Number.isInteger(document.version) ? document.version : 0);
        setLoadedAccessOverrides(normalizeAccessOverrides(document.accessOverrides));
        setSettings(prev => ({
          ...prev,
          allowFallback: document.defaults?.allowFallback ?? prev.allowFallback,
          defaultTheme: document.defaults?.defaultTheme ?? prev.defaultTheme,
          defaultLanguage: document.defaults?.defaultLanguage ?? prev.defaultLanguage,
          maxTotalTokens: document.defaults?.maxTotalTokens ?? prev.maxTotalTokens,
          vectorStore: document.defaults?.vectorStoreId ?? prev.vectorStore,
          embeddingModel: document.defaults?.embeddingProviderId ?? prev.embeddingModel,
          kbSimilarityThresholdPreset:
            document.defaults?.kbSimilarityThresholdPreset ?? prev.kbSimilarityThresholdPreset,
          kbSimilarityThreshold:
            document.defaults?.kbSimilarityThreshold ?? prev.kbSimilarityThreshold,
          enableSearch: document.coreModules?.search ?? prev.enableSearch,
          enableReports: document.coreModules?.reports ?? prev.enableReports,
          enableLiveReports: document.coreModules?.liveReports ?? prev.enableLiveReports,
          enableActions: document.coreModules?.actions ?? prev.enableActions,
          showKnowledgeBasePanel:
            document.workspacePanels?.knowledgeBase ?? prev.showKnowledgeBasePanel,
          showActionPanel: document.workspacePanels?.action ?? prev.showActionPanel,
          showAnalyticsHubPanel:
            document.workspacePanels?.analyticsHub ?? prev.showAnalyticsHubPanel,
          advancedSettings: document.advancedSettings
            ? (document.advancedSettings as AdvancedSettingsMap)
            : prev.advancedSettings
        }));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setSettingsVersion(0);
          setLoadedAccessOverrides(EMPTY_ACCESS_OVERRIDES);
          return;
        }
        console.error('Failed to load agent settings', error);
        setSettingsError('Failed to load agent settings.');
      })
      .finally(() => {
        if (!cancelled) setSettingsDocumentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [agentId]);

  useEffect(() => {
    let cancelled = false;

    const loadCatalogs = async () => {
      setCatalogLoading(true);
      setCatalogError(null);
      try {
        const [vectorRes, embeddingRes] = await Promise.all([
          ingestionService.getVectorStores(),
          embeddingProviderService.getList()
        ]);

        if (cancelled) return;

        const vectorList = normalizeApiList(vectorRes)
          .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
          .map(item => ({
            id: String(item.id ?? ''),
            label: vectorStoreLabel(item)
          }))
          .filter(opt => opt.id);

        const embeddingList = normalizeApiList(embeddingRes)
          .filter((item): item is EmbeddingProvider => {
            if (!item || typeof item !== 'object') return false;
            const provider = item as EmbeddingProvider;
            return Boolean(provider.id) && provider.isActive !== false;
          })
          .map(provider => ({
            id: provider.id,
            label: embeddingOptionLabel(provider)
          }));

        setVectorStoreOptions(vectorList);
        setEmbeddingOptions(embeddingList);

        setSettings(prev => {
          const nextVector =
            prev.vectorStore && vectorList.some(o => o.id === prev.vectorStore)
              ? prev.vectorStore
              : vectorList[0]?.id ?? '';
          const nextEmbedding =
            prev.embeddingModel && embeddingList.some(o => o.id === prev.embeddingModel)
              ? prev.embeddingModel
              : embeddingList[0]?.id ?? '';
          if (nextVector === prev.vectorStore && nextEmbedding === prev.embeddingModel) {
            return prev;
          }
          return { ...prev, vectorStore: nextVector, embeddingModel: nextEmbedding };
        });
      } catch (err) {
        console.error('Failed to load vector store / embedding catalogs', err);
        if (!cancelled) {
          setCatalogError('Failed to load vector stores or embedding providers.');
          setVectorStoreOptions([]);
          setEmbeddingOptions([]);
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };

    void loadCatalogs();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAccessCatalog = async () => {
      setAccessCatalogLoading(true);
      setAccessError(null);
      try {
        const [deptRes, userRes, roleRes] = await Promise.all([
          apiService.get(MASTERDATA_DEPARTMENTS_URL, {}, 'backend'),
          authservice.getUsers(),
          authservice.getRoles()
        ]);

        if (cancelled) return;

        setAccessCatalog({
          departments: parseDepartmentCatalog(deptRes),
          users: parseUserCatalog(userRes),
          roles: parseRoleCatalog(roleRes)
        });
      } catch (err) {
        console.error('Failed to load access catalogs', err);
        if (!cancelled) {
          setAccessError('Failed to load departments, users, or roles.');
          setAccessCatalog(EMPTY_ACCESS_CATALOG);
        }
      } finally {
        if (!cancelled) setAccessCatalogLoading(false);
      }
    };

    void loadAccessCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!agentId) {
      setAssignmentIds(null);
      setDepartments([]);
      setRoles([]);
      setUsers([]);
      return;
    }

    let cancelled = false;
    setAccessAssignmentsLoading(true);
    setAccessError(null);

    apiService
      .get(
        `${MASTERDATA_ASSIGNMENTS_PATH}/${CHAT_AGENT_MODULE_ID}/assignments`,
        { object_id: agentId },
        'backend'
      )
      .then((data: unknown) => {
        if (cancelled) return;
        setAssignmentIds(parseAssignmentsResponse(data));
      })
      .catch((err: unknown) => {
        console.error('Failed to load agent assignments', err);
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (err as Error)?.message ||
          'Failed to load assigned departments, users, or roles.';
        setAccessError(message);
        setAssignmentIds({ departments: [], users: [], roles: [] });
      })
      .finally(() => {
        if (!cancelled) setAccessAssignmentsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [agentId]);

  useEffect(() => {
    if (!assignmentIds) return;

    setDepartments(prev =>
      mapAssignmentIdsToEntries(
        assignmentIds.departments,
        id => {
          const dept = accessCatalog.departments.find(item => item.id === id);
          return { name: dept?.name ?? id, subtitle: dept?.name };
        },
        prev,
        loadedAccessOverrides.departments
      )
    );

    setRoles(prev =>
      mapAssignmentIdsToEntries(
        assignmentIds.roles,
        id => {
          const role = accessCatalog.roles.find(item => item.id === id);
          return {
            name: role?.name ?? id,
            subtitle: role?.description
          };
        },
        prev,
        loadedAccessOverrides.roles
      )
    );

    setUsers(prev =>
      mapAssignmentIdsToEntries(
        assignmentIds.users,
        id => {
          const user = accessCatalog.users.find(item => item.id === id);
          return {
            name: user?.name ?? id,
            subtitle: user?.email
          };
        },
        prev,
        loadedAccessOverrides.users
      )
    );
  }, [assignmentIds, accessCatalog, loadedAccessOverrides]);

  const accessLoading =
    accessCatalogLoading || accessAssignmentsLoading || settingsDocumentLoading;

  const patch = <K extends keyof AgentFeatureSettings>(key: K, value: AgentFeatureSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaveSuccess(false);
  };

  const handleKbSimilarityPresetChange = (presetId: string) => {
    if (presetId === KB_SIMILARITY_CUSTOM_ID) {
      patch('kbSimilarityThresholdPreset', KB_SIMILARITY_CUSTOM_ID);
      return;
    }
    const preset = KB_SIMILARITY_PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    setSettings(prev => ({
      ...prev,
      kbSimilarityThresholdPreset: presetId,
      kbSimilarityThreshold: preset.default
    }));
    setSaveSuccess(false);
  };

  const selectedKbPreset = KB_SIMILARITY_PRESETS.find(
    p => p.id === settings.kbSimilarityThresholdPreset
  );
  const isKbSimilarityCustom =
    settings.kbSimilarityThresholdPreset === KB_SIMILARITY_CUSTOM_ID;

  const patchLevelFilter = (
    level: AccessLevel,
    partial: Partial<LevelFilterState>
  ) => {
    setLevelFilters((prev) => ({
      ...prev,
      [level]: { ...prev[level], ...partial }
    }));
  };

  const clearLevelFilter = (level: AccessLevel) => {
    setLevelFilters((prev) => ({ ...prev, [level]: { ...EMPTY_LEVEL_FILTER } }));
  };

  const updateAccessList = (
    setter: React.Dispatch<React.SetStateAction<AccessEntry[]>>,
    id: string,
    updater: (access: AccessFlags) => AccessFlags
  ) => {
    setter((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, access: updater(entry.access) } : entry
      )
    );
    setSaveSuccess(false);
  };

  const handleCapabilityToggle = (
    level: AccessLevel,
    id: string,
    capability: AccessCapability,
    enabled: boolean
  ) => {
    const setter =
      level === 'departments' ? setDepartments : level === 'roles' ? setRoles : setUsers;
    updateAccessList(setter, id, (access) => ({ ...access, [capability]: enabled }));
  };

  const handleSetGroup = (
    level: AccessLevel,
    id: string,
    keys: AccessCapability[],
    grantAll: boolean
  ) => {
    const setter =
      level === 'departments' ? setDepartments : level === 'roles' ? setRoles : setUsers;
    updateAccessList(setter, id, (access) => {
      const next = { ...access };
      for (const key of keys) {
        next[key] = grantAll;
      }
      return next;
    });
  };

  const handleMaxTokensChange = (level: AccessLevel, id: string, maxTokens: number | null) => {
    const setter =
      level === 'departments' ? setDepartments : level === 'roles' ? setRoles : setUsers;
    setter((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, maxTokens } : entry))
    );
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaveSuccess(false);
    setSettingsError(null);

    const payload: UpdateChatAgentSettingsDto = {
      chatbotId: agentId,
      version: settingsVersion,
      defaults: {
        allowFallback: settings.allowFallback,
        defaultTheme: settings.defaultTheme,
        defaultLanguage: settings.defaultLanguage,
        maxTotalTokens: settings.maxTotalTokens,
        vectorStoreId: settings.vectorStore,
        embeddingProviderId: settings.embeddingModel,
        kbSimilarityThresholdPreset: settings.kbSimilarityThresholdPreset,
        kbSimilarityThreshold: settings.kbSimilarityThreshold
      },
      coreModules: {
        search: settings.enableSearch,
        reports: settings.enableReports,
        liveReports: settings.enableLiveReports,
        actions: settings.enableActions
      },
      workspacePanels: {
        knowledgeBase: settings.showKnowledgeBasePanel,
        action: settings.showActionPanel,
        analyticsHub: settings.showAnalyticsHubPanel
      },
      accessOverrides: {
        departments: departments.map(entry =>
          toAccessOverrideDto(entry, settings.maxTotalTokens)
        ),
        roles: roles.map(entry => toAccessOverrideDto(entry, settings.maxTotalTokens)),
        users: users.map(entry => toAccessOverrideDto(entry, settings.maxTotalTokens))
      },
      advancedSettings: { ...settings.advancedSettings }
    };

    const validationError = validateSettingsPayload(payload);
    if (validationError) {
      setSettingsError(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const response = await chatAgentService.updateSettings(agentId, payload);
      if (!response?.success || !response?.data) {
        throw new Error(response?.message || 'Failed to update agent settings.');
      }
      const savedDocument = response.data as ChatAgentSettingsDto;
      setSettingsVersion(
        Number.isInteger(savedDocument.version) ? savedDocument.version : settingsVersion + 1
      );
      setLoadedAccessOverrides(payload.accessOverrides);
      setSaveSuccess(true);
    } catch (error: unknown) {
      console.error('Failed to save agent settings', error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (error as Error)?.message ||
        'Failed to save agent settings.';
      setSettingsError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdvancedSettingsSave = (value: AdvancedSettingsMap) => {
    patch('advancedSettings', value);
  };

  const advancedSettingsCount = Object.keys(settings.advancedSettings).length;

  const levelTabs: { id: AccessLevel; label: string; icon: React.ReactNode; count: number }[] = [
    { id: 'departments', label: 'Departments', icon: <Building2 className="w-3.5 h-3.5" />, count: departments.length },
    { id: 'roles', label: 'Roles', icon: <Shield className="w-3.5 h-3.5" />, count: roles.length },
    { id: 'users', label: 'Users', icon: <Users className="w-3.5 h-3.5" />, count: users.length }
  ];

  const activeEntries =
    accessLevel === 'departments' ? departments : accessLevel === 'roles' ? roles : users;

  const activeFilter = levelFilters[accessLevel];
  const filteredEntries = useMemo(
    () => filterAccessEntries(activeEntries, activeFilter, accessLevel),
    [activeEntries, activeFilter, accessLevel]
  );
  const filterCopy = LEVEL_FILTER_COPY[accessLevel];
  const hasActiveFilter =
    activeFilter.query.trim() !== '' || activeFilter.grant !== 'all';

  const grantFilterOptions: { value: AccessGrantFilter; label: string }[] =
    accessLevel === 'departments'
      ? [
          { value: 'all', label: 'All departments' },
          { value: 'full', label: 'Full access' },
          { value: 'partial', label: 'Partial access' },
          { value: 'none', label: 'No access' }
        ]
      : accessLevel === 'roles'
        ? [
            { value: 'all', label: 'All roles' },
            { value: 'full', label: 'Full access' },
            { value: 'partial', label: 'Limited access' },
            { value: 'none', label: 'No access' }
          ]
        : [
            { value: 'all', label: 'All users' },
            { value: 'full', label: 'Full access' },
            { value: 'partial', label: 'Custom access' },
            { value: 'none', label: 'Blocked' }
          ];

  return (
    <div className={`h-full ${colors.background} overflow-auto flex flex-col`}>
      <div className={`px-6 py-4 border-b ${colors.border} ${colors.primary} flex-shrink-0`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`mt-0.5 p-2 rounded-md ${theme === 'light' ? 'bg-indigo-50' : 'bg-indigo-900/30'}`}>
              <SlidersHorizontal className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <h1 className={`text-lg font-semibold ${colors.text.primary}`}>Setting</h1>
              <p className={`text-sm ${colors.text.muted} mt-1`}>
                Configure agent defaults, core modules, workspace panels, and access overrides
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {saveSuccess && (
              <div className="flex items-center gap-2 text-green-500 text-sm">
                <Check className="w-4 h-4" />
                Saved
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowAdvancedModal(true)}
              title="Advanced settings"
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium border transition-colors ${colors.border} ${
                theme === 'light' ? 'bg-gray-100 text-gray-700' : 'bg-neutral-800 text-neutral-300'
              } ${colors.hover} ${colors.text.secondary}`}
            >
              <Settings2 className="w-4 h-4 text-indigo-400" />
              <span className="hidden sm:inline">Advanced</span>
              <span
                className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums ${
                  theme === 'light' ? 'bg-gray-200 text-gray-600' : 'bg-neutral-700 text-neutral-300'
                }`}
              >
                {advancedSettingsCount}
              </span>
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={
                isSaving ||
                settingsDocumentLoading ||
                accessCatalogLoading ||
                accessAssignmentsLoading ||
                catalogLoading
              }
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm rounded-md transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 p-6">
        <div className="max-w-5xl space-y-5">
          {settingsError && (
            <div className="flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-950/20 px-3 py-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-rose-400" />
              <p className="text-sm text-rose-300">{settingsError}</p>
            </div>
          )}

          {/* Group 1 Agent Defaults */}
          <SettingGroup
            title="Agent Defaults"
            description="Fallback behavior, theme, language, retrieval configuration, and token budget"
            colors={colors}
          >
            <div className={`flex items-start justify-between gap-4 py-3 border-b ${colors.border}`}>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${colors.text.primary} flex items-center gap-1.5`}>
                  <Coins className="w-3.5 h-3.5 text-indigo-400" />
                  Chatbot total tokens
                </p>
                <p className={`text-xs ${colors.text.muted} mt-0.5`}>
                  Maximum tokens this chatbot can consume overall. Override caps below inherit this when left empty.
                </p>
              </div>
              <div className="w-44 shrink-0">
                <input
                  id="agent-max-total-tokens"
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={formatTokenLimitInput(settings.maxTotalTokens)}
                  onChange={(e) => patch('maxTotalTokens', parseTokenLimit(e.target.value))}
                  placeholder="Unlimited"
                  aria-label="Chatbot total tokens"
                  className={`w-full ${colors.input} border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                />
                <p className={`text-[10px] mt-1 text-right ${colors.text.muted}`}>
                  {settings.maxTotalTokens == null
                    ? 'No cap'
                    : settings.maxTotalTokens.toLocaleString()}
                </p>
              </div>
            </div>
            <ToggleRow
              label="Allow Fallback"
              description={
                settingsDocumentLoading
                  ? 'Loading fallback preference…'
                  : 'Enable fallback responses when no RAG match is found (non-RAG agents)'
              }
              enabled={settings.allowFallback}
              onChange={(value) => patch('allowFallback', value)}
              theme={theme}
              colors={colors}
            />
            <div className={`flex items-start justify-between gap-4 py-3 border-b ${colors.border}`}>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${colors.text.primary}`}>Vector Store</p>
                <p className={`text-xs ${colors.text.muted} mt-0.5`}>
                  Vector database used for knowledge retrieval and semantic search
                </p>
              </div>
              <select
                value={settings.vectorStore}
                onChange={(e) => patch('vectorStore', e.target.value)}
                aria-label="Vector store"
                disabled={catalogLoading || vectorStoreOptions.length === 0}
                className={`w-56 shrink-0 ${colors.input} border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60`}
              >
                {catalogLoading ? (
                  <option value="">Loading…</option>
                ) : vectorStoreOptions.length === 0 ? (
                  <option value="">No vector stores found</option>
                ) : (
                  <>
                    <option value="">Select vector store…</option>
                    {vectorStoreOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
            <div className={`flex items-start justify-between gap-4 py-3 border-b ${colors.border}`}>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${colors.text.primary}`}>Embedding Model</p>
                <p className={`text-xs ${colors.text.muted} mt-0.5`}>
                  Embedding provider used to embed documents and user queries for vector search
                </p>
              </div>
              <select
                value={settings.embeddingModel}
                onChange={(e) => patch('embeddingModel', e.target.value)}
                aria-label="Embedding model"
                disabled={catalogLoading || embeddingOptions.length === 0}
                className={`w-56 shrink-0 ${colors.input} border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60`}
              >
                {catalogLoading ? (
                  <option value="">Loading…</option>
                ) : embeddingOptions.length === 0 ? (
                  <option value="">No embedding providers found</option>
                ) : (
                  <>
                    <option value="">Select embedding provider…</option>
                    {embeddingOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
            {catalogError && (
              <p className="py-2 text-xs text-rose-400 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                {catalogError}
              </p>
            )}
            <div className={`py-3 border-b ${colors.border}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${colors.text.primary}`}>
                    ORCHESTRATOR_KB_SIMILARITY_THRESHOLD
                  </p>
                  <p className={`text-xs ${colors.text.muted} mt-0.5`}>
                    Dense similarity threshold for knowledge-base retrieval
                  </p>
                  {(selectedKbPreset || isKbSimilarityCustom) && (
                    <p className={`text-[11px] mt-1.5 ${colors.text.muted}`}>
                      {isKbSimilarityCustom
                        ? `Custom value between ${KB_SIMILARITY_CUSTOM_MIN} and ${KB_SIMILARITY_CUSTOM_MAX}`
                        : selectedKbPreset?.behavior}
                    </p>
                  )}
                </div>
                <div className="w-full sm:w-72 shrink-0 space-y-2">
                  <select
                    value={settings.kbSimilarityThresholdPreset}
                    onChange={(e) => handleKbSimilarityPresetChange(e.target.value)}
                    aria-label="KB similarity threshold preset"
                    className={`w-full ${colors.input} border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  >
                    {KB_SIMILARITY_PRESETS.map(preset => (
                      <option key={preset.id} value={preset.id}>
                        {preset.label} ({formatSimilarityRange(preset.min, preset.max)})
                      </option>
                    ))}
                    <option value={KB_SIMILARITY_CUSTOM_ID}>Custom</option>
                  </select>

                  {isKbSimilarityCustom ? (
                    <div>
                      <input
                        type="number"
                        min={KB_SIMILARITY_CUSTOM_MIN}
                        max={KB_SIMILARITY_CUSTOM_MAX}
                        step={0.01}
                        value={settings.kbSimilarityThreshold}
                        onChange={e => {
                          const parsed = parseSimilarityInput(e.target.value);
                          if (parsed != null) patch('kbSimilarityThreshold', parsed);
                        }}
                        aria-label="Custom KB similarity threshold"
                        className={`w-full ${colors.input} border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                      <p className={`text-[10px] mt-1 ${colors.text.muted}`}>
                        Min {KB_SIMILARITY_CUSTOM_MIN} · Max {KB_SIMILARITY_CUSTOM_MAX}
                      </p>
                    </div>
                  ) : (
                    selectedKbPreset && (
                      <div
                        className={`rounded-md border px-3 py-2 text-xs ${colors.border} ${
                          theme === 'light' ? 'bg-gray-50' : 'bg-neutral-900/40'
                        }`}
                      >
                        <span className={colors.text.muted}>Active threshold: </span>
                        <span className={`font-semibold tabular-nums ${colors.text.primary}`}>
                          {settings.kbSimilarityThreshold.toFixed(2)}
                        </span>
                        <span className={`ml-2 ${colors.text.muted}`}>
                          (suggested {formatSimilarityRange(selectedKbPreset.min, selectedKbPreset.max)})
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
            <div className={`flex items-start justify-between gap-4 py-3 border-b ${colors.border}`}>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${colors.text.primary}`}>Default Theme</p>
                <p className={`text-xs ${colors.text.muted} mt-0.5`}>
                  Initial appearance for the agent experience
                </p>
              </div>
              <SegmentedControl
                ariaLabel="Default theme"
                value={settings.defaultTheme}
                onChange={(v) => patch('defaultTheme', v as DefaultTheme)}
                colors={colors}
                theme={theme}
                options={[
                  { value: 'dark', label: 'Dark' },
                  { value: 'light', label: 'Light' }
                ]}
              />
            </div>
            <div className={`flex items-start justify-between gap-4 py-3`}>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${colors.text.primary}`}>Default Language</p>
                <p className={`text-xs ${colors.text.muted} mt-0.5`}>
                  Primary language for the agent UI and responses
                </p>
              </div>
              <SegmentedControl
                ariaLabel="Default language"
                value={settings.defaultLanguage}
                onChange={(v) => patch('defaultLanguage', v as DefaultLanguage)}
                colors={colors}
                theme={theme}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'ar', label: 'Arabic (AR)' }
                ]}
              />
            </div>
          </SettingGroup>

          {/* Group 2 Core Modules (defaults) */}
          <SettingGroup
            title="Core Modules"
            description="Agent capabilities enabled and working by default"
            colors={colors}
            bodyClassName="px-0 py-0"
          >
            <div
              className={`mx-4 mt-3 mb-1 flex items-start gap-2 rounded-md border px-3 py-2 ${
                theme === 'light'
                  ? 'border-emerald-200 bg-emerald-50'
                  : 'border-emerald-800/60 bg-emerald-950/30'
              }`}
            >
              <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${theme === 'light' ? 'text-emerald-600' : 'text-emerald-400'}`} />
              <p className={`text-xs ${theme === 'light' ? 'text-emerald-800' : 'text-emerald-300'}`}>
                These modules work by default. Use Access Overrides below to restrict who can use each capability.
              </p>
            </div>
            <div className="px-4 py-1">
              <ToggleRow
                label="Search"
                description="On by default search across agent knowledge and content"
                enabled={settings.enableSearch}
                onChange={(v) => patch('enableSearch', v)}
                theme={theme}
                colors={colors}
              />
              <ToggleRow
                label="Reports"
                description="On by default report viewing and generation"
                enabled={settings.enableReports}
                onChange={(v) => patch('enableReports', v)}
                theme={theme}
                colors={colors}
              />
              <ToggleRow
                label="Live Reports"
                description="On by default live report capabilities"
                enabled={settings.enableLiveReports}
                onChange={(v) => patch('enableLiveReports', v)}
                theme={theme}
                colors={colors}
              />
              <ToggleRow
                label="Actions"
                description="On by default trigger configured actions"
                enabled={settings.enableActions}
                onChange={(v) => patch('enableActions', v)}
                theme={theme}
                colors={colors}
              />
            </div>
          </SettingGroup>

          {/* Group 3 Workspace Panels (defaults) */}
          <SettingGroup
            title="Workspace Panels"
            description="Default panel visibility enabled and working by default"
            colors={colors}
            bodyClassName="px-0 py-0"
          >
            <div
              className={`mx-4 mt-3 mb-1 flex items-start gap-2 rounded-md border px-3 py-2 ${
                theme === 'light'
                  ? 'border-emerald-200 bg-emerald-50'
                  : 'border-emerald-800/60 bg-emerald-950/30'
              }`}
            >
              <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${theme === 'light' ? 'text-emerald-600' : 'text-emerald-400'}`} />
              <p className={`text-xs ${theme === 'light' ? 'text-emerald-800' : 'text-emerald-300'}`}>
                These panels are available by default. Use Access Overrides below to restrict panel access per department, role, or user.
              </p>
            </div>
            <div className="px-4 py-1">
              <ToggleRow
                label="Knowledge Base Panel"
                description="On by default show the knowledge base side panel"
                enabled={settings.showKnowledgeBasePanel}
                onChange={(v) => patch('showKnowledgeBasePanel', v)}
                theme={theme}
                colors={colors}
              />
              <ToggleRow
                label="Action Panel"
                description="On by default show the actions side panel"
                enabled={settings.showActionPanel}
                onChange={(v) => patch('showActionPanel', v)}
                theme={theme}
                colors={colors}
              />
              <ToggleRow
                label="Analytics Hub Reports Panel"
                description="On by default show the analytics hub reports panel"
                enabled={settings.showAnalyticsHubPanel}
                onChange={(v) => patch('showAnalyticsHubPanel', v)}
                theme={theme}
                colors={colors}
              />
            </div>
          </SettingGroup>

          {/* Group 4 Access Overrides */}
          <SettingGroup
            title="Access Overrides"
            description="Modules, panels, and token caps per department, role, and user"
            colors={colors}
            bodyClassName="px-0 py-0"
            collapsible
            defaultExpanded={false}
          >
            <div className={`flex flex-wrap gap-2 px-4 py-3 border-b ${colors.border}`}>
              {levelTabs.map((tab) => {
                const active = accessLevel === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setAccessLevel(tab.id)}
                    className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      active
                        ? 'bg-indigo-600 text-white'
                        : `${colors.text.secondary} ${colors.secondary} border ${colors.border} ${colors.hover}`
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                        active
                          ? 'bg-indigo-700 text-indigo-100'
                          : theme === 'light'
                            ? 'bg-gray-200 text-gray-600'
                            : 'bg-neutral-700 text-neutral-300'
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {!agentId && (
              <div className={`mx-4 mt-3 flex items-start gap-2 rounded-md border px-3 py-2 ${colors.border} ${colors.secondary}`}>
                <AlertCircle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${colors.text.muted}`} />
                <p className={`text-xs ${colors.text.muted}`}>
                  Save this chat agent first to load assigned departments, roles, and users.
                </p>
              </div>
            )}

            {accessError && (
              <div className="mx-4 mt-3 flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-950/20 px-3 py-2">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-rose-400" />
                <p className="text-xs text-rose-300">{accessError}</p>
              </div>
            )}

            {agentId && accessLoading && (
              <div className={`flex items-center justify-center gap-2 py-10 ${colors.text.muted}`}>
                <Loader2 className="w-4 h-4 animate-spin" />
                <p className="text-sm">Loading access assignments…</p>
              </div>
            )}

            {agentId && !accessLoading && (
            <>
            <div className={`px-4 py-3 border-b ${colors.border} space-y-2`}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative flex-1 min-w-0">
                  <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${colors.text.muted}`} />
                  <input
                    type="search"
                    value={activeFilter.query}
                    onChange={(e) => patchLevelFilter(accessLevel, { query: e.target.value })}
                    placeholder={filterCopy.placeholder}
                    aria-label={filterCopy.placeholder}
                    className={`w-full ${colors.input} border rounded-md pl-8 pr-8 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  />
                  {activeFilter.query && (
                    <button
                      type="button"
                      onClick={() => patchLevelFilter(accessLevel, { query: '' })}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 ${colors.text.muted} hover:text-indigo-400`}
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <select
                  value={activeFilter.grant}
                  onChange={(e) =>
                    patchLevelFilter(accessLevel, {
                      grant: e.target.value as AccessGrantFilter
                    })
                  }
                  aria-label={filterCopy.grantLabel}
                  className={`sm:w-44 ${colors.input} border rounded-md px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                >
                  {grantFilterOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {hasActiveFilter && (
                  <button
                    type="button"
                    onClick={() => clearLevelFilter(accessLevel)}
                    className={`text-xs font-medium text-indigo-400 hover:text-indigo-300 whitespace-nowrap`}
                  >
                    Clear filters
                  </button>
                )}
              </div>
              <p className={`text-[11px] ${colors.text.muted}`}>
                Showing {filteredEntries.length} of {activeEntries.length}{' '}
                {accessLevel}
                {hasActiveFilter ? ' (filtered)' : ''}
              </p>
            </div>

            <div className="px-4 py-1">
              <AccessLevelBlock
                entries={filteredEntries}
                onToggle={(id, capability, enabled) =>
                  handleCapabilityToggle(accessLevel, id, capability, enabled)
                }
                onSetGroup={(id, keys, grantAll) =>
                  handleSetGroup(accessLevel, id, keys, grantAll)
                }
                onMaxTokensChange={(id, maxTokens) =>
                  handleMaxTokensChange(accessLevel, id, maxTokens)
                }
                agentMaxTokens={settings.maxTotalTokens}
                theme={theme}
                colors={colors}
                emptyLabel={
                  hasActiveFilter
                    ? `No ${accessLevel} match the current filters`
                    : `No assigned ${accessLevel} for this agent`
                }
              />
            </div>
            </>
            )}
          </SettingGroup>
        </div>
      </div>

      <AdvancedSettingsModal
        open={showAdvancedModal}
        theme={theme}
        colors={colors}
        value={settings.advancedSettings}
        onClose={() => setShowAdvancedModal(false)}
        onSave={handleAdvancedSettingsSave}
      />
    </div>
  );
};

export default ChatAgentSetting;
